import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMemberships } from '../actions/membershipActions';
import MembershipCard from '../components/MembershipCard';

// GENERATED for HR without Borders from the client's SaaS brief — see
// BUILD-NOTES.md. The copy below is theirs; the bracketed bits are placeholders.

const SERVICES = [
  { icon: "📅", title: "What you can do here", body: "Visitors will discover the HR Without Borders mission, understand what the community offers, explore key topics and upcoming conversations, and join the community." },
  { icon: "🚀", title: "How it reaches you", body: "The community will be delivered primarily through our website , supported by LinkedIn as the main channel for discovery, thought leadership and community growth." },
  { icon: "📅", title: "Book it online", body: "Community discussions, live virtual roundtables, expert conversations, international HR insights, practical resources, member stories and an events calendar." },
  { icon: "💬", title: "Stay in the loop", body: "Content will focus on Global HR, AI & the Future of Work, People & Culture, leadership, talent and HR practices across countries." },
  { icon: "✅", title: "Done means done", body: "Members will know the community is valuable when they have built genuine international HR relationships, received useful perspectives on real challenges, learned from…" },
  { icon: "💬", title: "Reach us your way", body: "Visitors can connect with HR Without Borders through LinkedIn, join the community directly through Skool, or contact the community through the website." },
  { icon: "💬", title: "Reach out", body: "Once inside, members can participate in discussions, message other members, ask questions, suggest topics and connect directly with the community…" },
];

const STEPS = [
  { n: "01", title: "HR professionals discover HR Without Borders", body: "HR professionals discover HR Without Borders primarily through LinkedIn, where conversations around international HR, AI, leadership…" },
  { n: "02", title: "Join the community", body: "They then join the community and introduce themselves, their country, experience and areas of interest." },
  { n: "03", title: "Inside the community", body: "Inside the community, members connect with HR peers around the world, ask questions, exchange country-specific practices and discuss real…" },
];

const TESTIMONIALS = [
  { name: "[First customer]", city: "HR professionals", item: "Membership · A global community where HR…", quote: "Replace with a real quote once you have one." },
  { name: "[Second customer]", city: "People leaders", item: "Membership · Before → after", quote: "A short, specific before/after beats a generic testimonial." },
  { name: "[Third customer]", city: "HR consultants", item: "Membership · Live in days", quote: "Never borrow anyone else’s outcome — earn one and put it here." },
];

export default function Home() {
  const dispatch = useDispatch();
  const { items } = useSelector(s => s.memberships);
  useEffect(() => { dispatch(fetchMemberships({ limit: 6 })); }, [dispatch]);

  return (
    <>
      <section className="hero">
        <div className="hero-inner">
          <p className="hero-eyebrow">{"A global community where HR professionals connect"}</p>
          <h1>{"HR Without Borders is a global community where HR professionals connect across countries"}</h1>
          <p className="hero-sub">{"HR Without Borders is a global community where HR professionals connect across countries, cultures and industries to navigate the changing world of work together."}</p>
          <div className="hero-cta">
            <Link to="/inventory" className="btn-primary">{"Join now"}</Link>
            <a href="#services" className="btn-ghost">What we do</a>
          </div>
        </div>
      </section>

      <section className="section demo-banner" aria-label="Demo credentials">
        <div className="demo-banner-inner">
          <div className="demo-banner-tag">DEMO MODE</div>
          <div className="demo-banner-body">
            <p><strong>This is the HR without Borders app, built on the Free Builder kit.</strong> Log in with one of the demo accounts below, then seed demo data from the Admin panel.</p>
            <ul className="demo-creds">
              <li><span className="role admin">Admin</span><code>admin@hrwithout.demo</code> / <code>demo1234</code></li>
              <li><span className="role manager">Manager</span><code>manager@hrwithout.demo</code> / <code>demo1234</code></li>
              <li><span className="role sales">Staff</span><code>sales@hrwithout.demo</code> / <code>demo1234</code></li>
              <li><span className="role customer">Customer</span><code>customer@hrwithout.demo</code> / <code>demo1234</code></li>
            </ul>
            <p className="demo-banner-fine">Same passwords. Same database. Roles gate different parts of the UI. <Link to="/docs">Read the docs</Link> to see how each module is wired.</p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="section-head">
          <h2>Latest memberships</h2>
          <Link to="/inventory">See all →</Link>
        </div>
        <div className="grid grid-3">
          {items.map(x => <MembershipCard key={x._id} membership={x} />)}
          {items.length === 0 && <p className="muted">Nothing yet. (Admins: seed demo data from the Admin panel.)</p>}
        </div>
      </section>

      <section className="section" id="services">
        <div className="section-head"><h2>What we do for you</h2></div>
        <p className="section-lead">{"HR Without Borders is a global community where HR professionals connect across countries, cultures and industries to navigate the changing world of work together."}</p>
        <div className="grid grid-4 services-grid">
          {SERVICES.map(s => (
            <div key={s.title} className="service-card">
              <span className="service-icon" aria-hidden="true">{s.icon}</span>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section" id="how-it-works">
        <div className="section-head"><h2>How it works</h2></div>
        <div className="grid grid-3">
          {STEPS.map(s => (
            <div key={s.n} className="step"><span className="step-num">{s.n}</span><h3>{s.title}</h3><p>{s.body}</p></div>
          ))}
        </div>
      </section>

      <section className="section" id="testimonials">
        <div className="section-head"><h2>What customers say</h2></div>
        <div className="grid grid-3">
          {TESTIMONIALS.map(t => (
            <figure key={t.name} className="testimonial">
              <blockquote>"{t.quote}"</blockquote>
              <figcaption>
                <strong>{t.name}</strong>
                <span className="muted"> · {t.city}</span>
                <div className="testimonial-car">{t.item}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="section cta-band" id="visit">
        <div className="cta-band-inner">
          <div>
            <h2>{"Ready for your first membership?"}</h2>
            <p>{"Visitors can connect with HR Without Borders through LinkedIn, join the community directly through Skool, or contact the community through…"}</p>
          </div>
          <div className="cta-band-actions">
            <Link to="/inventory" className="btn-primary">{"Join now"}</Link>
            <a href="tel:+33647655878" className="btn-ghost">Call +33.647655878</a>
          </div>
        </div>
      </section>
    </>
  );
}
